/**
 * Client-side Page Designer persistence.
 * No database, no EMR records — localStorage only.
 *
 * Init rule: saved layout exists → restore it; otherwise blank 60×40.
 * Reset is in-memory only and does not write or clear storage.
 *
 * Schema v3 is grid-only: a card is `col`/`row`/`width`/`height` in cells
 * of the 60×40 page and nothing else. There are no panels and no pixel
 * offsets to persist.
 *
 * Content Functions are additive within v3: a card carries one configuration
 * field per function (`rtfHtml`, `tableContent`, `searchContent`). A v3 save
 * written before a function existed simply has no value for it and normalizes
 * to that function's empty configuration, so adding one does not bump the
 * version or invalidate a saved layout.
 *
 * Older saves are migrated on load and never discarded (see
 * migrateLegacyLayout). Nothing is rewritten to storage until the next
 * explicit Save.
 */

import {
  BEHAVIOR_NONE,
  CELL_SIZE,
  CONTENT_TYPE_BLANK,
  GRID_COLUMNS,
  GRID_ROWS,
  clampCard,
  defaultCards,
  normalizeLayout,
} from "@/lib/pageDesignerLayout";
import { emptySearchContent, sanitizeSearchContent } from "@/lib/pageDesignerSearch";
import { emptyTableContent, sanitizeTableContent } from "@/lib/pageDesignerTable";

export const PAGE_DESIGNER_STORAGE_KEY = "emrbymd.v3.pageDesigner.layout";
export const PAGE_DESIGNER_STORAGE_VERSION = 3;

/**
 * Every pre-v3 save used a 30×20 page whose design cell was 48px. The
 * page's design size never changed (30 × 48 = 60 × 24 = 1440), so one
 * legacy cell is exactly two of today's, and a legacy pixel offset is
 * exactly `offset / CELL_SIZE` of today's cells.
 */
const LEGACY_GRID_SCALE = 2;

export function serializeLayout(cards) {
  return JSON.stringify({
    version: PAGE_DESIGNER_STORAGE_VERSION,
    grid: {
      columns: GRID_COLUMNS,
      rows: GRID_ROWS,
      cellSize: CELL_SIZE,
    },
    cards: (cards || []).map((card) => ({
      id: card.id,
      title: card.title,
      col: card.col,
      row: card.row,
      width: card.width,
      height: card.height,
      background: card.background,
      border: card.border,
      contentType: card.contentType || CONTENT_TYPE_BLANK,
      behavior: card.behavior || BEHAVIOR_NONE,
      rtfHtml: card.rtfHtml || "",
      tableContent: sanitizeTableContent(card.tableContent || emptyTableContent()),
      searchContent: sanitizeSearchContent(card.searchContent || emptySearchContent()),
    })),
  });
}

function asInt(value, fallback) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

/** Legacy pixel offset → whole cells of the current grid. */
function offsetCells(value) {
  return Math.round((Number(value) || 0) / CELL_SIZE);
}

/**
 * Converts one pre-v3 card to grid-only page geometry.
 *
 * Two things have to be undone. A v2 card was positioned inside its own
 * panel's local coordinate space, so its col/row are translated into page
 * coordinates using that panel's own origin (a panel always kept
 * `columns === width`, so the cell size is the same on both sides and the
 * translation is exact). Then the whole thing is scaled onto the finer
 * grid and each pixel offset is folded into the nearest cell — which is
 * the point of the finer grid: an offset that used to need sub-cell
 * pixels now resolves to a real cell.
 *
 * Nothing here can fail. Anything that still lands outside the page is
 * clamped by normalizeLayout, so a legacy save can never erase a card.
 */
function migrateLegacyCard(card, panelsById) {
  const panel = panelsById?.get(card?.panelId);
  const legacyCol = asInt(card?.col, 1) + (panel ? asInt(panel.col, 1) - 1 : 0);
  const legacyRow = asInt(card?.row, 1) + (panel ? asInt(panel.row, 1) - 1 : 0);
  const { panelId, offsetX, offsetY, widthOffset, heightOffset, ...rest } = card || {};
  return {
    ...rest,
    col: (legacyCol - 1) * LEGACY_GRID_SCALE + 1 + offsetCells(offsetX),
    row: (legacyRow - 1) * LEGACY_GRID_SCALE + 1 + offsetCells(offsetY),
    width: asInt(card?.width, 1) * LEGACY_GRID_SCALE + offsetCells(widthOffset),
    height: asInt(card?.height, 1) * LEGACY_GRID_SCALE + offsetCells(heightOffset),
  };
}

/**
 * Migrates any pre-v3 save — the oldest bare array, v1's flat cards, or
 * v2's panels + panel-relative cards — onto the grid-only model.
 */
function migrateLegacyLayout(rawCards, rawPanels) {
  const panelsById = new Map(
    (Array.isArray(rawPanels) ? rawPanels : [])
      .filter((panel) => typeof panel?.id === "string")
      .map((panel) => [panel.id, panel]),
  );
  const migrated = (Array.isArray(rawCards) ? rawCards : []).map((card) =>
    clampCard(migrateLegacyCard(card, panelsById)),
  );
  return normalizeLayout(migrated);
}

/**
 * Parses saved storage into a cards array, migrating older formats.
 * Returns null when nothing usable was saved.
 */
export function parseSavedLayout(raw) {
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (Array.isArray(parsed)) return migrateLegacyLayout(parsed);
  if (!parsed || typeof parsed !== "object" || !parsed.cards) return null;
  // Only versions BELOW v3 are pre-grid and need rescaling. A payload from
  // a newer build is already grid-only, so read it as-is: doubling it
  // would corrupt every card if an older build ever reads a newer save.
  if (asInt(parsed.version, 0) >= PAGE_DESIGNER_STORAGE_VERSION) return normalizeLayout(parsed.cards);
  // v1/v2, or any older/unversioned object shape.
  return migrateLegacyLayout(parsed.cards, parsed.panels);
}

export function loadSavedLayout() {
  if (typeof localStorage === "undefined") return null;
  return parseSavedLayout(localStorage.getItem(PAGE_DESIGNER_STORAGE_KEY));
}

/**
 * Persists the layout and returns exactly what landed in storage, so the
 * caller can adopt it as both its live state and its saved snapshot. That
 * is what keeps live state, snapshot and storage identical after a Save —
 * a Save can never leave the page looking dirty, and can never quietly
 * store something different from what is on screen.
 *
 * Returns null only when there is no localStorage to write to.
 */
export function saveLayout(cards) {
  if (typeof localStorage === "undefined") return null;
  const normalized = normalizeLayout(cards);
  localStorage.setItem(PAGE_DESIGNER_STORAGE_KEY, serializeLayout(normalized));
  return normalized;
}

export function clearSavedLayout() {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(PAGE_DESIGNER_STORAGE_KEY);
}

/** Blank 60×40 default. Does not change localStorage. */
export function resetLayout() {
  return defaultCards();
}

/**
 * Opening snapshot: restore the last Save, or the blank grid when nothing is saved.
 * An explicitly saved empty layout (`[]`) is distinct from "no saved layout".
 */
export function initialLayout() {
  const loaded = loadSavedLayout();
  return loaded == null ? defaultCards() : loaded;
}

export function initialSavedSnapshot() {
  const loaded = loadSavedLayout();
  if (loaded == null) return null;
  return loaded.map((card) => ({ ...card }));
}
